import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { collection, query, where, getDocs, doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from './useAuth';

export interface Companion {
  id: string;
  name: string;
  avatar: string | null;
  deeds: Record<string, number>;
}

export function useCompanions() {
  const { session } = useAuth();
  const [companions, setCompanions] = useState<Companion[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCompanions = async () => {
    if (!session) return;
    
    try {
      // Get IDs of people I am following
      const q = query(collection(db, 'companions'), where('user_id', '==', session.uid));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const friendIds = querySnapshot.docs.map(doc => doc.data().friend_id);
        
        // Fetch their profiles (Firestore 'in' query has max 10 limit, doing multiple reads here)
        const friends: Companion[] = [];
        for (const fId of friendIds) {
          const profileDoc = await getDoc(doc(db, 'profiles', fId));
          if (profileDoc.exists()) {
            friends.push(profileDoc.data() as Companion);
          }
        }
        
        setCompanions(friends);
      } else {
        setCompanions([]);
      }
    } catch (err) {
      console.error('Error fetching companions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanions();
  }, [session]);

  const addCompanion = async (friendId: string) => {
    if (!session) return { error: 'Not logged in' };
    if (friendId === session.uid) return { error: 'You cannot add yourself' };

    try {
        // Check if profile exists first
        const profileDoc = await getDoc(doc(db, 'profiles', friendId));
            
        if (!profileDoc.exists()) return { error: 'User not found. Ask them to log in to Habl.' };

        // Add companion relation (use composite key for doc ID)
        const relationId = `${session.uid}_${friendId}`;
        await setDoc(doc(db, 'companions', relationId), {
            user_id: session.uid,
            friend_id: friendId
        });
        
        fetchCompanions(); // Refresh list
        return { error: null };
    } catch (err: any) {
        return { error: err.message };
    }
  };

  const removeCompanion = async (friendId: string) => {
    if (!session) return;
    
    try {
        const relationId = `${session.uid}_${friendId}`;
        await deleteDoc(doc(db, 'companions', relationId));
            
        fetchCompanions();
    } catch (err) {
        console.error('Error removing companion:', err);
    }
  };

  return { companions, loading, addCompanion, removeCompanion, refresh: fetchCompanions };
}
